// lib
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';

// elvispos
import { BaseService, Helpers } from 'ngx-elvis-ds';
import { SAMService } from '../sam/sam.service';
import { ConfigService } from '../config.service';
import { SAMMessage } from '../sam/interfaces/sam-message';
import { MenusService } from './menus.service';
import { Platform, ModalController, ToastController } from '@ionic/angular';
import { DeviceType } from '../base/device-type.enum';
import { ConfirmDialogPage } from '../gui/gui-dialogs/confirm-dialog/confirm-dialog.page';
import { ModalOptions } from '@ionic/core';
import { SpeechService } from './speech.service';
import { WindowRef } from '../window-ref.service';
import { I18NService } from './i18n.service';

@Injectable({
  providedIn: 'root'
})
export class CartService extends BaseService {

  pluToast: HTMLIonToastElement;

  lastStoreId: number;
  lastDeviceId: number;

  cart: any[] = [];
  bestSellers: any[] = [];
  pendingCart: any[] = [];

  qtyMode = 'DEFAULT';
  qtyItems = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
  counters: { qty?: number, } = { qty: 1 };

  totals: {
    total?: number,
    totalDiscount?: number,
    due?: number,
    itemsCount?: number,
    discounts?: any[]
  } = {
    total: 0,
    totalDiscount: 0,
    itemsCount: 0,
    discounts: []
  };

  saleTime = 0;
  variantCache: String;

  get itemsCount() {
    const reducer = (accumulator, item) => accumulator + parseInt(item.Qty, 10);

    if (this.cart.length > 0) {
      return this.cart.reduce(reducer, 0);
    } else {
      return 0;
    }
  }

  get lastItem() {
    return ( this.cart && this.cart[this.cart.length-1] );
  }

  constructor(
    private config: ConfigService,
    private sam: SAMService,
    private configSvc: ConfigService,
    private storage: Storage,
    private menusSvc: MenusService,
    private platform: Platform,
    private modalCtrl: ModalController,
    private toastCtrl: ToastController,
    private speechSvc: SpeechService,
    private winRef: WindowRef,
    private i18nSvc: I18NService
  ) {
    super();
    platform.ready().then( async () => {
      await this.getCartStorage();
      await this.getBSStorage();
    });
  }

  onSetQtyMode(mode: string) {
    this.sam.onSend({Command: 'CMD_SET_QTY_MODE', parms: { Mode: mode }});
  }

  onRequestPayment() {
    this.sam.onSend({ Command: 'TENDER' });
  }

  onVoidAll() {
    this.sam.onSend({ Command: 'VOID_ALL' });
  }

  onVoidBarcode() {
    this.sam.onSend({ Command: 'VOID_BARCODE' });
  }

  onItemLookup(item) {
    if (this.winRef.scrolling) {
      return;
    }

    this.sam.onSend({
      Command: 'LOOKUP',
      Val: item.sz_item_code || item.Code,
      parms: {
        Kitchen: item.n0_kitchen_id,
        Qty: this.counters.qty.toString(),
        ActiveMenu: this.menusSvc.activeMenu ? this.menusSvc.activeMenu.n0_id : null
      }
    });
    this.counters.qty = 1;
  }

  onItemQtyEdit(item) {
    this.sam.onSend({
      Command: 'QUANTITY',
      Val: `${item.ItemInd},${item.Qty}`,
      parms: {
        id: String(item.ItemInd),
        qty: String(item.Qty),
        itemInd: String(item.ItemInd),
      }
    });
  }

  onItemQtySet(item, newQuantity) {
    this.sam.onSend({
      Command: 'SET_ITEM_QTY',
      Val: String(newQuantity),
      parms: {
        index: String(item.ItemInd),
        usrInp: String(newQuantity),
      }
    });
  }

  onItemPriceEdit(item) {
    this.sam.onSend({
      Command: 'PRICE',
      Val: `${item.ItemInd},${item.Price}`,
      parms: {
        id: String(item.ItemInd),
        price: String(item.Price),
        itemInd: String(item.ItemInd),
      }
    });
  }

  onItemDiscountEdit(item) {
    this.sam.onSend({
      Command: 'DISCOUNT',
      Val: `${item.ItemInd},${item.Discount}`,
      parms: {
        id: String(item.ItemInd),
        disc: String(item.Discount),
        itemInd: String(item.ItemInd),
      }
    });
  }

  onItemVoid(item) {
    this.sam.onSend({
      Command: 'VOID_PLU',
      Val: `${item.ItemInd}`,
      parms: {
        id: String(item.ItemInd),
        itemInd: String(item.ItemInd)
      }
    });
  }

  onQuantityChange(changeAmount, set = false) {
    if (!set) {
      if (changeAmount !== 0) {
        this.counters.qty = Math.max(this.counters.qty + changeAmount, 1);
      } else {
        this.sam.onSend({ Command: 'CMD_ASK_QUANTITY' });
      }
    } else {
      if (changeAmount > 0) {
        this.counters.qty = Math.max(changeAmount, 1);
      }
    }
  }

  onFollowUpItem() {
    try {
      this.sam.onSend({
        Command: 'LOOKUP',
        Val: this.config.cartSettings.followUpItem.code,
        parms: {
          Kitchen: this.config.cartSettings.followUpItem.kitchen,
          Qty: 1
        }
      });
    } catch (error) {
      
    }
  }

  onToggleItem(cartItem) {
    if (this.winRef.scrolling) {
      return;
    }

    if (!cartItem.disabled) {
      cartItem.selected = !cartItem.selected;
      this.sam.onSend({
        Command: 'CMD_PLU_CHECK',
        parms: {
          ItemInd: cartItem.ItemInd,
          Checked: cartItem.selected
        }
      });
    }
  }

  onSelectItem(cartItem) {
    if (cartItem.Type !== 'plu') {
      return;
    }

    if (this.winRef.scrolling) {
      return;
    }

    const itemCode = cartItem.Code.trim();
    let followUpItemCode;

    if (this.configSvc.cartSettings.followUpItem) {
      followUpItemCode = this.configSvc.cartSettings.followUpItem.code;
    }

    if (!cartItem.disabled && itemCode !== followUpItemCode) {
      this.sam.onSend({
        Command: 'VARIANTE',
        Val: cartItem.ItemInd,
        parms: {
          ItemInd: cartItem.ItemInd,
          Id: cartItem.ItemInd
        }
      });
    }
  }

  computeBestSeller(item) {
    const bestSeller = {...item};

    // TODO: fix this, no hardcoded stuff
    const DEPARTMENT_TYPE_ID = 7;

    if (item.ItemType !== DEPARTMENT_TYPE_ID) {
      const bestSellersIds = this.bestSellers.map(bs => bs.Code);
      if (bestSellersIds.indexOf(item.Code) === -1) {
        bestSeller.soldCount = 1;
        this.bestSellers.push(bestSeller);
      } else {
        this.updateBSSoldCount(item.Code, 1);
      }
      this.setBestSellers();
    }
  }

  private updateBSSoldCount(code, newCount) {
    const bs = this.bestSellers.find(bs => {
      return bs.Code === code;
    });
    bs.soldCount++;
  }

  private setBestSellers() {
    const compare = (a, b) => {
      if (a.soldCount < b.soldCount) {
        return 1;
      }
      if (a.soldCount > b.soldCount) {
        return -1;
      }
      return 0;
    }
    this.bestSellers = this.bestSellers.sort(compare).slice(0,10);
    this.setBSStorage();
  }

  async subscribeListeners() {
    this.sam.channels.auth.subscribe((msg: SAMMessage) => {
      if (['READY_TO_SELL', 'READY_TO_ADMIN'].includes(msg.Command)) {
        const data = msg.parms;
        if (this.lastDeviceId !== data.DeviceId || this.lastStoreId !== data.StoreId) {
          this.bestSellers = [];
          this.setBestSellers();
        }
        this.lastDeviceId = data.DeviceId;
        this.lastStoreId = data.StoreId;
      }
    });

    this.sam.channels.cart.subscribe(async (msg: SAMMessage) => {

      // SET_QTY_MODE
      if (['SET_QTY_MODE'].includes(msg.Command)) {
        const data: { Mode: string } = msg.parms;
        this.qtyMode = data.Mode;
      }

      // SET_QUANTITY
      if (['SET_QUANTITY'].includes(msg.Command)) {
        const data: { Val: number } = msg.parms;
        this.counters.qty = data.Val;
      }

      // GUI_TOTALS
      if (['GUI_TOTALS'].includes(msg.Command)) {
        const data: any = msg.parms;

        this.totals = {
          total: data.n0Total / this.configSvc.currency.divider,
          totalDiscount: data.n0Discount / this.configSvc.currency.divider,
          discounts: data.TotalDisc,
          itemsCount: parseInt(data.ItemCnt, 0)
        };

        if (this.totals.totalDiscount > 0) {
          this.events.next();
        }
      }

      // On PLU Command add new Item in Cart
      if (['PLU', 'ADD_TO_CART'].includes(msg.Command)) {
        const data: any = msg.parms;
        
        if (!data.Type) {
          data.Type = 'plu';
        }

        // if PLU is a plu
        if (data.Type === 'plu') {
          data.Price = Helpers.priceToFloat(data.Price);

          if (data.OriginalPrice !== undefined) {
            data.Price = data.OriginalPrice;
          }
  
          if (data.Code && this.config.cartSettings.followUpItem && this.config.cartSettings.followUpItem.code) {
            data.followUpItem = data.Code.trim() === this.config.cartSettings.followUpItem.code.trim();
          }
        }

        // add line to cart array and save in storage
        this.cart.push(data);
        this.setCartStorage(this.cart);

        // if PLU is a plu
        if (data.Type === 'plu') {
          if (!data.NoVelCode && this.configSvc.guiSettings.displayBestSellers) {
            this.computeBestSeller(data);
          }

          if (window.innerWidth < 720) {
            if (this.pluToast) {
              await this.pluToast.dismiss();
            }

            let msg = `${data.Descr.trim()}: aggiunto al carrello!`;
            let color = `success`;
            
            if (data.followUpItem) {
              msg = `Aggiunto ${data.Descr.trim()}, prossima portata...`;
              color = `tertiary`;
            }

            this.pluToast = await this.toastCtrl.create({
              message: msg,
              duration: 1000,
              position: 'bottom',
              color: color,
              cssClass: 'custom-plu-toast'
            });

            this.pluToast.present();
          }

          if (this.config.deviceType !== DeviceType.SCO) {
            this.speechSvc.say(this.speechSvc.clean(data.Descr.trim()));
          }

          this.counters.qty = 1;
          this.saleTime += data.SaleTime;
        }

        this.events.next('cart:changed');
      }

      // QUANTITY
      if (['QUANTITY'].includes(msg.Command)) {
        const data: any = msg.parms;

        const item = this.cart.find((ci: any) => {
          return String(ci.ItemInd) === String(data.Id);
        });

        item.Qty = parseInt(data.QUANTITY, 0);
      }

      // DISCOUNT
      if (['DISCOUNT'].includes(msg.Command)) {
        const data: any = msg.parms;

        const item = this.cart.find((ci: any) => {
          return String(ci.ItemInd) === String(data.Id);
        });

        item.Discount = Helpers.priceToFloat(data.Discount);
      }

      // DISCOUNTS
      if (['DISCOUNTS'].includes(msg.Command)) {
        const discounts: any = msg.parms;

        for (const discount of discounts) {
          const cartItem = this.cart.find((ci: any) => {
            return String(ci.ItemInd) === String(discount.Id);
          });
  
          cartItem.Discount = Helpers.priceToFloat(discount.Discount);
        }
      }

      // PRICE
      if (['PRICE'].includes(msg.Command)) {
        const data: any = msg.parms;

        const item = this.cart.find((ci: any) => {
          return String(ci.ItemInd) === String(data.Id);
        });

        item.Price = Helpers.priceToFloat(data.PRICE);
      }

      // CMD_VARIANT_APPLIED
      if (['CMD_VARIANT_APPLIED'].includes(msg.Command)) {
        const data: any = msg.parms;

        const item = this.cart.find((ci: any) => {
          return String(ci.ItemInd) === String(data.Id);
        });

        if (data.Value) {
          item.Variant = data.Value;
        } else {
          item.Variant = this.variantCache;
        }
        this.variantCache = null;
        this.counters.qty = 1;
      }

      // VOID_PLU
      if (['VOID_PLU'].includes(msg.Command)) {
        const data: any = msg.parms;

        this.cart = this.cart.filter((item: any) => String(item.ItemInd) !== String(data.Id));
        this.counters.qty = 1;

        this.events.next('cart:changed');
      }
    });

    this.sam.channels.gui.subscribe((msg: SAMMessage) => {
      // CLEAN
      if (['CLEAN'].includes(msg.Command)) {
        this.cart = [];
        this.setCartStorage(null);
        this.totals = {
          total: 0,
          totalDiscount: 0,
          itemsCount: 0,
          discounts: []
        };
        this.counters.qty = 1;
        this.events.next('cart:changed');
      }
    });
  }

  // private

  async checkForPendingCart() {
    if (this.pendingCart && this.pendingCart.length === 0) {
      return;
    }

    setTimeout(async () => {
      if (
        this.pendingCart &&
        this.config.cartSettings.enableRecovery &&
        this.config.deviceType !== DeviceType.RESTAURANT &&
        this.config.deviceType !== DeviceType.BAR_SELF &&
        this.config.deviceType !== DeviceType.SCO
      ) {
        const modalOptions: ModalOptions = {
          component: ConfirmDialogPage,
          backdropDismiss: false,
          cssClass: 'confirm-dialog fullscreen',
          componentProps: {
            data: {
              Title: this.i18nSvc.translate('general.suspended_cart'),
              Question: this.i18nSvc.translate('general.suspended_cart_message'),
            },
            isLocal: true
          }
        };
  
        const modal = await this.modalCtrl.create(modalOptions);
  
        modal.onDidDismiss().then((resp) => {
          if (resp.data) {
            this.applyCartStorage();
          } else {
            this.dropCartStorage();
          }
        });
  
        await modal.present();
      }
    }, 500)

  }

  private setBSStorage() {
    this.storage.set('bestSellers', {
      bestSellers: this.bestSellers,
      lastDeviceId: this.lastDeviceId,
      lastStoreId: this.lastStoreId
    });
  }

  private async getBSStorage() {
    const storage = await this.storage.get('bestSellers');
    if (storage) {
      this.bestSellers = storage.bestSellers;
      this.lastDeviceId = storage.lastDeviceId;
      this.lastStoreId = storage.lastStoreId;
      this.setBestSellers();
    }
  }
  
  /**
   * Retrieve Cart from LocalStorage
   *
   * @private
   * @memberof CartService
   */
  private async getCartStorage() {
    this.pendingCart = await this.storage.get('cart');
  }

  /**
   * Store current Cart to LocalStorage
   *
   * @private
   * @param {any[]} pendingCart
   * @memberof CartService
   */
  private setCartStorage(pendingCart: any[]) {
    this.storage.set('cart', pendingCart);
  }

  /**
   * Drop stored Cart in LocalStorage
   *
   * @private
   * @memberof CartService
   */
  private dropCartStorage() {
    this.setCartStorage(null);
  }

  /**
   * Apply stored Card from LocalStorage and release from memory
   *
   * @private
   * @memberof CartService
   */
  private applyCartStorage() {
    for (const product of this.pendingCart) {
      this.sam.onSend({
        Command: 'LOOKUP',
        Val: product.Code.trim(),
        parms: {
          Qty: product.Qty
        }
      });
      this.setCartStorage(null);
    }
  }
}
